import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { readEdgeError } from "@/lib/edgeError";
import { toast } from "sonner";
import {
  ArrowDown,
  ArrowUp,
  Brain,
  ChevronDown,
  CircleDot,
  Clock,
  FlaskConical,
  GitBranch,
  LockKeyhole,
  Plus,
  Repeat,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectItemDescription,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import WorkflowKnowledgePanel from "./WorkflowKnowledgePanel";
import PlanFeatureGate from "@/components/admin_dashboard/PlanFeatureGate";
import { useAuth } from "@/contexts/AuthContext";
import { planHasFeature } from "@/lib/planEntitlements";
import { onlyWordingChanged } from "@/lib/workflows/templateStructure";
import {
  ACTION_LABELS,
  BOOKING_TRIGGER_EVENTS,
  CHANNEL_LABELS,
  TRIGGER_EVENT_DESCRIPTIONS,
  ACTION_DESCRIPTIONS,
  ACTION_GROUPS,
  TRIGGER_EVENT_GROUPS,
  TRIGGER_EVENT_LABELS,
  enclosingLoop,
  loopSpans,
  normalizeExceptions,
  toDisplaySteps,
  withExplicitMessageRecipients,
  type Binding,
  type ExecutionAction,
  type LoopSpan,
  type NodeType,
  type OutputField,
  type Predicate,
  type PredicateGroup,
  type TriggerEvent,
  type WorkflowDefinition,
  type WorkflowEdge,
  type WorkflowNode,
} from "@/lib/workflows/schema";
import {
  ALWAYS_APPROVE_ACTIONS,
  CONTEXT_FIELD_CATALOG,
  LOOP_ALLOWED_ACTIONS,
  decisionFieldDefs,
  type ContextFieldDef,
  MAX_LOOP_ITERATIONS,
  MAX_NOTE_TITLE_CHARS,
  MAX_RESCHEDULE_DAYS,
  MAX_SESSION_FINISHED_HOURS,
  SUPPORTED_ACTIONS,
  SUPPORTED_TRIGGER_EVENTS,
  canIncludeNextBlock,
  includeNextBlock,
  insertBlock,
  moveBlock,
  releaseLastBlock,
  removeBlock,
  validateForPublish,
  type DryRunScenario,
  type DryRunStep,
} from "@/lib/workflows/catalog";

// WorkflowBuilder. The editable canvas behind "Create workflow".
//
// Vertical block-coding surface: the same five primitives the read-only
// diagram renders, now insertable/configurable/reorderable. The logic flow an
// owner draws here is FIXED at publish; the AI's only latitude at runtime is
// the bounded TIME (aiAdjustable waits) and CONTENT (Decision nodes)
// parameters. All persistence goes through the workflow-definitions edge
// function so validation + caps are server-enforced.

interface WorkflowBuilderProps {
  businessId: string;
  /** Row id of the draft to edit, or "new" for a blank workflow. */
  workflowId: string;
  onClose: () => void;
  /** Called with the row id after a successful save of a NEW workflow. */
  onSavedNew: (id: string) => void;
  /** Keeps the admin breadcrumb in sync with the editable workflow name. */
  onNameChange?: (name: string | null) => void;
  /** Opens another admin section without making the owner hunt through the sidebar. */
  onNavigateToSection?: (section: string, extraParams?: Record<string, string>) => void;
}

const BLOCK_STYLES: Record<string, { icon: LucideIcon; chip: string; label: string; blurb: string }> = {
  trigger: { icon: Zap, chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Trigger", blurb: "The event that starts the workflow." },
  condition: { icon: GitBranch, chip: "bg-blue-50 text-blue-700 border-blue-200", label: "Control", blurb: "Guide the workflow with conditions and loops — choose a path or repeat steps until a condition is met." },
  decision: { icon: Brain, chip: "bg-violet-50 text-violet-700 border-violet-200", label: "Decision", blurb: "The AI writes personalised content for a later step." },
  execution: { icon: Send, chip: "bg-primary/10 text-primary border-primary/20", label: "Execution", blurb: "The action the workflow carries out." },
  delay: { icon: Clock, chip: "bg-muted text-muted-foreground border-border", label: "Wait", blurb: "A time gap before the next step." },
  // The second Control block, so it shares the Control colour.
  loop: { icon: Repeat, chip: "bg-blue-50 text-blue-700 border-blue-200", label: "Repeat until", blurb: "Repeat the blocks inside it until a stop rule matches, e.g. nudge every 2 days until they reply." },
};

/** The "Add a block" menu. The two Control blocks sit under one heading. */
const INSERT_OPTIONS: ReadonlyArray<{ type: NodeType; group?: string; label: string; blurb: string }> = [
  { type: "condition", group: "Control", label: "If / else", blurb: "Carry on only when the checks pass, or skip ahead." },
  { type: "loop", group: "Control", label: "Repeat until", blurb: "Repeat blocks until a stop rule matches, e.g. nudge until they reply." },
  { type: "decision", label: BLOCK_STYLES.decision.label, blurb: BLOCK_STYLES.decision.blurb },
  { type: "execution", label: BLOCK_STYLES.execution.label, blurb: BLOCK_STYLES.execution.blurb },
  { type: "delay", label: BLOCK_STYLES.delay.label, blurb: BLOCK_STYLES.delay.blurb },
];

/** The stop rule a new loop starts with: the one almost every loop wants. */
const DEFAULT_STOP_RULE: PredicateGroup = {
  label: "Customer has replied (since the last workflow message)",
  all: [{
    field: "conversation.hasReplied",
    operator: "is_true",
    label: "Customer has replied (since the last workflow message)",
  }],
};

/** Title a freshly-inserted Trigger block carries before an event is chosen. */
const DEFAULT_TRIGGER_TITLE = "When a booking is created";

/** business_message_templates.status, as the template picker shows it. */
const TEMPLATE_STATUS_LABEL: Record<string, string> = {
  draft: "draft, not sent to Meta",
  submitted: "waiting for Meta",
  rejected: "rejected by Meta",
  paused: "paused",
};

/**
 * A Trigger's card title follows the chosen event UNTIL the owner types their
 * own. Leaving a stale "When a booking is created" above a message trigger is
 * the single most confusing thing the panel can show, but silently renaming a
 * title the owner wrote would be worse, so only a title that is still one of
 * OUR defaults gets replaced.
 */
function isUntouchedTriggerTitle(
  label: string | undefined,
  previousEvent: string,
): boolean {
  const current = (label ?? "").trim();
  if (!current) return true;
  if (current === DEFAULT_TRIGGER_TITLE) return true;
  return current ===
    TRIGGER_EVENT_LABELS[previousEvent as TriggerEvent];
}

/** Fresh node of a given type with sensible defaults. */
function blankNode(type: NodeType): WorkflowNode {
  const id = `${type}-${Date.now().toString(36)}${Math.floor(Math.random() * 36).toString(36)}`;
  switch (type) {
    case "condition":
      return { id, type, label: "If-else condition", parameters: { exceptions: [] } };
    case "decision":
      return {
        id,
        type,
        label: "Craft message",
        parameters: {
          goal: "",
          output: [{ key: "message", type: "string", description: "The message to send." }],
        },
      };
    case "execution":
      return {
        id,
        type,
        label: "Send message",
        parameters: {
          action: "send_message",
          recipient: "workflow_subject",
          message: { source: "static", value: "" },
        },
      };
    case "delay":
      return { id, type, label: "Wait", parameters: { mode: "duration", durationMinutes: 24 * 60 } };
    case "loop":
      // Its blocks are added alongside it (insertBlock's `body`).
      return { id, type, label: "Repeat until", parameters: { stopWhen: [DEFAULT_STOP_RULE] as unknown as WorkflowNode["parameters"][string], maxIterations: 3 } };
    default:
      return { id, type, label: DEFAULT_TRIGGER_TITLE, parameters: { event: "booking_created" } };
  }
}

function blankDefinition(): WorkflowDefinition {
  return {
    id: "new",
    key: "custom_new",
    name: "Untitled workflow",
    description: "",
    version: 1,
    nodes: [blankNode("trigger")],
  };
}

/** Duration as value+unit for friendlier editing than raw minutes. The unit
 *  is a pure DISPLAY choice (local state): switching it re-renders the same
 *  total in the new unit. It never reinterprets the number, so a 1-day wait
 *  viewed in hours shows 24, not silently becoming 1 hour. */
const UNIT_MINUTES: Record<string, number> = { minutes: 1, hours: 60, days: 24 * 60 };
const deriveUnit = (minutes: number) =>
  minutes % (24 * 60) === 0 && minutes >= 24 * 60 ? "days" : minutes % 60 === 0 && minutes >= 60 ? "hours" : "minutes";

const DurationField = ({
  minutes,
  onChange,
  label,
}: {
  minutes: number;
  onChange: (mins: number) => void;
  label: string;
}) => {
  const [unit, setUnit] = useState(() => deriveUnit(minutes));
  const factor = UNIT_MINUTES[unit] ?? 1;
  // Round for display; a 90-min wait viewed in hours shows 1.5.
  const value = Math.round((minutes / factor) * 100) / 100;
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-normal text-muted-foreground">{label}</Label>
      <div className="flex gap-2">
        <Input
          type="number"
          min={0}
          step="any"
          value={value}
          onChange={(e) => onChange(Math.max(0, Math.round(Number(e.target.value) * factor)))}
          className="h-9"
        />
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger className="h-9 w-28 shrink-0">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="minutes">minutes</SelectItem>
            <SelectItem value="hours">hours</SelectItem>
            <SelectItem value="days">days</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

/** Auto-label for a predicate row, used unless the owner writes their own. */
function predicateAutoLabel(p: Predicate, fields: ContextFieldDef[] = CONTEXT_FIELD_CATALOG): string {
  const field = fields.find((f) => f.field === p.field);
  const name = field?.label ?? p.field;
  switch (p.operator) {
    case "is_true": return name;
    case "is_false": return `NOT ${name}`;
    case "within_days": return `${name} is within ${p.value ?? "?"} days`;
    case "eq": return `${name} is "${p.value ?? "?"}"`;
    case "neq": return `${name} is not "${p.value ?? "?"}"`;
    case "gt": return `${name} is more than ${p.value ?? "?"}`;
    case "lte": return `${name} is at most ${p.value ?? "?"}`;
    case "contains": return `${name} include "${p.value ?? "?"}"`;
    default: return name;
  }
}

/** Auto-label for a whole rule: its AND-ed predicates joined. */
function groupAutoLabel(g: PredicateGroup): string {
  return g.all.map((p) => p.label).filter(Boolean).join(" and ");
}

const OPERATOR_LABELS: Record<string, string> = {
  is_true: "is true",
  is_false: "is false",
  within_days: "within the last … days",
  eq: "equals",
  neq: "does not equal",
  gt: "is more than",
  lte: "is at most",
  contains: "include",
};

/** Grouping for the condition field picker, keyed by catalog prefix. */
const CONTEXT_FIELD_GROUPS: ReadonlyArray<{ label: string; prefix: string }> = [
  { label: "Conversation", prefix: "conversation." },
  { label: "Customer", prefix: "customer." },
  { label: "Booking", prefix: "booking." },
  { label: "Forms & CRM", prefix: "form." },
];

/** Starting points for the Decision goal. The jobs service businesses
 *  actually run. Clicking one seeds the goal text; the owner edits freely. */
const GOAL_PRESETS: ReadonlyArray<{ label: string; goal: string }> = [
  {
    label: "Win-back",
    goal: "Write a warm, personal note inviting them back — reference what they've enjoyed before, no pressure.",
  },
  {
    label: "First-visit welcome",
    goal: "Welcome them after their first session, ask how it went, and mention what a natural next step would be.",
  },
  {
    label: "Review ask",
    goal: "Thank them for their visit and ask — lightly, once — if they'd share a quick review.",
  },
  {
    label: "Late-cancel note",
    goal: "Acknowledge their cancellation kindly and make rebooking feel easy — never guilt them.",
  },
];

/** The string outputs a send can bind to on a Decision node. */
const stringOutputs = (n: WorkflowNode | undefined): OutputField[] =>
  (((n?.parameters.output as unknown as OutputField[]) ?? [])).filter((f) => f.type === "string");

const NodeHandle = ({ position }: { position: "top" | "bottom" }) => (
  <span
    className={`absolute left-1/2 z-10 h-2.5 w-2.5 -translate-x-1/2 rounded-full border border-border bg-background ${
      position === "top" ? "-top-[5px]" : "-bottom-[5px]"
    }`}
  />
);

const Connector = ({ onInsert, allowLoop = true }: { onInsert?: (type: NodeType) => void; allowLoop?: boolean }) => (
  <div className="flex justify-center">
    <div className="relative flex h-10 w-full items-center justify-center">
      <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-border" />
      {onInsert ? (
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="z-10 flex h-6 w-6 items-center justify-center rounded-full border border-dashed border-border bg-background text-muted-foreground/70 transition-colors hover:border-primary hover:text-primary"
              aria-label="Insert a block here"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-72 p-2" align="center">
            <p className="px-2 pb-1.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70">
              Add a block
            </p>
            {/* Loops don't nest, so a slot inside a loop offers no loop. */}
            {INSERT_OPTIONS.filter((option) => allowLoop || option.type !== "loop").map((option, index, options) => {
              const s = BLOCK_STYLES[option.type];
              const Icon = s.icon;
              const heading = option.group && options[index - 1]?.group !== option.group;
              return (
                <div key={option.type}>
                  {heading && (
                    <p className="px-2 pb-0.5 pt-1 text-[11px] font-medium text-muted-foreground">{option.group}</p>
                  )}
                  <button
                    type="button"
                    onClick={() => onInsert(option.type)}
                    className="flex w-full items-start gap-2.5 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted"
                  >
                    <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${s.chip}`}>
                      <Icon className="h-3.5 w-3.5" />
                    </span>
                    <span>
                      <span className="block text-sm font-medium text-foreground">{option.label}</span>
                      <span className="block text-xs text-muted-foreground">{option.blurb}</span>
                    </span>
                  </button>
                </div>
              );
            })}
          </PopoverContent>
        </Popover>
      ) : (
        <ChevronDown className="absolute bottom-0 left-1/2 h-4 w-4 -translate-x-1/2 text-muted-foreground/40" strokeWidth={2} />
      )}
    </div>
  </div>
);

const FlowCap = ({ label }: { label: string }) => (
  <div className="flex justify-center">
    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-3 py-1 text-[11px] font-medium text-muted-foreground">
      <CircleDot className="h-3 w-3" />
      {label}
    </span>
  </div>
);

/** The rule list Conditions and loops share: rules are OR-ed, the checks
 *  inside one rule AND-ed. */
const RulesEditor = ({
  rules,
  onChange,
  aiFields = [],
}: {
  rules: PredicateGroup[];
  onChange: (next: PredicateGroup[]) => void;
  /** Results of AI steps above this block (decisionFieldDefs). */
  aiFields?: ContextFieldDef[];
}) => {
  const setRules = onChange;
  const allFields = [...CONTEXT_FIELD_CATALOG, ...aiFields];
  const patchPredicate = (ri: number, qi: number, patch: Partial<Predicate>) => {
    const next = rules.map((g) => ({ ...g, all: [...g.all] }));
    const merged = { ...next[ri].all[qi], ...patch } as Predicate;
    merged.label = predicateAutoLabel(merged, allFields);
    next[ri].all[qi] = merged;
    next[ri].label = groupAutoLabel(next[ri]);
    setRules(next);
  };
  const blankPredicate = (): Predicate => {
    const first = CONTEXT_FIELD_CATALOG[0];
    const p: Predicate = { field: first.field, operator: first.operators[0] as Predicate["operator"], label: "" };
    p.label = predicateAutoLabel(p);
    return p;
  };
  return (
    <>
      {rules.map((g, ri) => (
        <div key={ri} className="space-y-2 rounded-lg border border-border p-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-medium text-muted-foreground">Rule {ri + 1}</span>
            <button
              type="button"
              onClick={() => setRules(rules.filter((_, i) => i !== ri))}
              className="rounded p-1 text-muted-foreground/60 hover:text-destructive"
              aria-label="Remove rule"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          {g.all.map((p, qi) => {
            const fieldDef = allFields.find((f) => f.field === p.field);
            const needsValue = ["within_days", "eq", "neq", "gt", "gte", "lt", "lte", "contains"].includes(p.operator);
            return (
              <div key={qi} className="space-y-2">
                {qi > 0 && <p className="text-[11px] font-medium text-muted-foreground">…and</p>}
                <div className="flex items-start gap-2">
                  <div className="min-w-0 flex-1 space-y-2">
                    <Select value={p.field} onValueChange={(v) => {
                      const fd = allFields.find((f) => f.field === v);
                      patchPredicate(ri, qi, { field: v, operator: (fd?.operators[0] ?? "is_true") as Predicate["operator"], value: undefined });
                    }}>
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Pick what to check">{fieldDef?.label}</SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {CONTEXT_FIELD_GROUPS.map((grp) => {
                          const fields = CONTEXT_FIELD_CATALOG.filter((f) => f.field.startsWith(grp.prefix));
                          if (fields.length === 0) return null;
                          return (
                            <SelectGroup key={grp.label}>
                              <SelectLabel>{grp.label}</SelectLabel>
                              {fields.map((f) => (
                                <SelectItem key={f.field} value={f.field}>
                                  <span className="block">{f.label}</span>
                                  {f.description && (
                                    <SelectItemDescription>{f.description}</SelectItemDescription>
                                  )}
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          );
                        })}
                        {aiFields.length > 0 && (
                          <SelectGroup>
                            <SelectLabel>AI step results</SelectLabel>
                            {aiFields.map((f) => (
                              <SelectItem key={f.field} value={f.field}>
                                <span className="block">{f.label}</span>
                                {f.description && (
                                  <SelectItemDescription>{f.description}</SelectItemDescription>
                                )}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        )}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2">
                      <Select value={p.operator} onValueChange={(v) => patchPredicate(ri, qi, { operator: v as Predicate["operator"] })}>
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(fieldDef?.operators ?? ["is_true", "is_false"]).map((op) => (
                            <SelectItem key={op} value={op}>{OPERATOR_LABELS[op] ?? op}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {needsValue && (
                        <Input
                          value={String(p.value ?? "")}
                          placeholder={fieldDef?.valueHint ?? "value"}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const num = Number(raw);
                            // Tags compare as text. Never coerce for `contains`.
                            const coerce = p.operator !== "contains" && raw !== "" && !isNaN(num);
                            patchPredicate(ri, qi, { value: coerce ? num : raw });
                          }}
                          className="h-9"
                        />
                      )}
                    </div>
                  </div>
                  {g.all.length > 1 && (
                    <button
                      type="button"
                      onClick={() => {
                        const next = rules.map((x) => ({ ...x, all: [...x.all] }));
                        next[ri].all = next[ri].all.filter((_, i) => i !== qi);
                        next[ri].label = groupAutoLabel(next[ri]);
                        setRules(next);
                      }}
                      className="mt-2 rounded p-1 text-muted-foreground/60 hover:text-destructive"
                      aria-label="Remove this check"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <button
            type="button"
            onClick={() => {
              const next = rules.map((x) => ({ ...x, all: [...x.all] }));
              next[ri].all.push(blankPredicate());
              next[ri].label = groupAutoLabel(next[ri]);
              setRules(next);
            }}
            className="text-[11px] font-medium text-primary hover:underline"
          >
            + and also…
          </button>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="w-full gap-1.5"
        onClick={() => {
          const p = blankPredicate();
          setRules([...rules, { label: p.label, all: [p] }]);
        }}
      >
        <Plus className="h-3.5 w-3.5" />
        Add a rule
      </Button>
    </>
  );
};

const WorkflowBuilder = ({
  businessId,
  workflowId,
  onClose,
  onSavedNew,
  onNameChange,
  onNavigateToSection,
}: WorkflowBuilderProps) => {
  const queryClient = useQueryClient();
  const isNew = workflowId === "new";
  const { subscriptionTier } = useAuth();
  // Plans without custom workflows (Starter, Sandbox) use Dibs templates as-is:
  // titles and message wording can change, the steps can't. The server
  // enforces the same rule at publish.
  const stepsLocked = !planHasFeature(subscriptionTier, "custom_workflows");

  const { data: row, isLoading } = useQuery({
    queryKey: ["workflow-definition", workflowId],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("workflow_definitions")
        .select("*")
        .eq("id", workflowId)
        .maybeSingle();
      return data ?? null;
    },
    enabled: !isNew,
  });

  // The business's services, for the trigger's service filter picker.
  const { data: listings } = useQuery({
    queryKey: ["workflow-builder-listings", businessId],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("listings")
        .select("listing_id, name")
        .eq("business_id", businessId)
        .order("name");
      return (data ?? []) as Array<{ listing_id: string; name: string }>;
    },
  });

  const { data: tallyForms } = useQuery({
    queryKey: ["workflow-builder-tally-forms", businessId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("tally_form_connections")
        .select("tally_form_id, form_name, is_enabled")
        .eq("business_id", businessId)
        .order("form_name");
      if (error) throw error;
      return (data ?? []) as Array<{
        tally_form_id: string;
        form_name: string;
        is_enabled: boolean;
      }>;
    },
  });

  // WhatsApp templates for a Send message step to fall back on once the
  // customer's 24-hour window has closed. Every status is listed so a step can
  // be wired up before Meta approves; the engine only ever sends approved ones.
  const { data: workflowTemplates } = useQuery({
    queryKey: ["workflow-builder-wa-templates", businessId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("business_message_templates")
        .select("id, provider_template_name, status, status_note, managed")
        .eq("business_id", businessId)
        .eq("channel", "whatsapp")
        .eq("purpose", "workflow")
        .order("provider_template_name");
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        provider_template_name: string;
        status: string;
        status_note: string | null;
        managed: boolean;
      }>;
    },
  });

  const [def, setDef] = useState<WorkflowDefinition>(blankDefinition);
  const [name, setName] = useState("Untitled workflow");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<string>("draft");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<null | "save" | "publish" | "dry_run" | "draft" | "preview">(null);
  const [dryRun, setDryRun] = useState<DryRunStep[] | null>(null);
  const [testScenario, setTestScenario] = useState<NonNullable<DryRunScenario["conditionOutcomes"]>>({});
  const [testLoopRounds, setTestLoopRounds] = useState<NonNullable<DryRunScenario["loopRounds"]>>({});
  const [preview, setPreview] = useState<
    null | { nodeId: string; output: Record<string, unknown>; sample: Record<string, unknown> }
  >(null);
  const [aiDescription, setAiDescription] = useState("");

  useEffect(() => {
    if (row) {
      setDef(withExplicitMessageRecipients(row.definition as WorkflowDefinition));
      setName(row.name);
      setDescription(row.description ?? "");
      setStatus(row.status);
      setTestScenario({});
      setTestLoopRounds({});
    }
  }, [row]);

  useEffect(() => {
    onNameChange?.(name);
  }, [name, onNameChange]);

  useEffect(() => () => onNameChange?.(null), [onNameChange]);

  const issues = useMemo(() => validateForPublish({ ...def, name, description }), [def, name, description]);
  const displaySteps = useMemo(() => toDisplaySteps(def), [def]);
  const spans = useMemo(() => loopSpans(def), [def]);
  const isComplaintWorkflow = def.nodes.some(
    (node) => node.type === "trigger" && node.parameters.event === "complaint_received",
  );
  const selected = def.nodes.find((n) => n.id === selectedId) ?? null;
  const decisionsBefore = (nodeId: string) => {
    const idx = def.nodes.findIndex((n) => n.id === nodeId);
    return def.nodes.slice(0, idx).filter((n) => n.type === "decision");
  };

  // Drop edges a node change invalidated (removed endpoints, or a reorder
  // that made a skip-edge point backward. Publish would reject it anyway).
  const sanitizeEdges = (nodes: WorkflowNode[], edges?: WorkflowEdge[]) => {
    if (!edges?.length) return undefined;
    const idx = new Map(nodes.map((n, i) => [n.id, i] as const));
    const kept = edges.filter((e) => {
      const from = idx.get(e.from);
      if (from === undefined) return false;
      if (e.to === "end") return true;
      const to = idx.get(e.to);
      return to !== undefined && to > from;
    });
    return kept.length ? kept : undefined;
  };
  const mutate = (fn: (nodes: WorkflowNode[]) => WorkflowNode[]) => {
    setDef((d) => {
      const nodes = fn(d.nodes);
      return { ...d, nodes, edges: sanitizeEdges(nodes, d.edges) };
    });
    setDirty(true);
  };
  /** Set or clear a Condition's "skip to" branch (its only authorable edge). */
  const setSkipEdge = (nodeId: string, targetId: string | null) => {
    if (stepsLocked) return;
    setDef((d) => {
      const rest = (d.edges ?? []).filter((e) => !(e.from === nodeId && e.on === "skip"));
      const edges = targetId ? [...rest, { from: nodeId, to: targetId, on: "skip" as const }] : rest;
      return { ...d, edges: edges.length ? edges : undefined };
    });
    setDirty(true);
  };
  const updateNode = (id: string, patch: (n: WorkflowNode) => WorkflowNode) =>
    mutate((nodes) => nodes.map((n) => {
      if (n.id !== id) return n;
      const next = patch(n);
      return stepsLocked && !onlyWordingChanged(n, next) ? n : next;
    }));
  const setParam = (id: string, key: string, value: unknown) =>
    updateNode(id, (n) => {
      const parameters = { ...n.parameters } as Record<string, unknown>;
      if (value === undefined) delete parameters[key];
      else parameters[key] = value;
      return { ...n, parameters: parameters as WorkflowNode["parameters"] };
    });

  // Whole-definition edits: inserting, moving and removing blocks must keep
  // every loop's last block (its bodyEnd) in step, so they go through the
  // shared loop-aware helpers rather than raw array splices.
  const mutateDef = (fn: (d: WorkflowDefinition) => WorkflowDefinition) => {
    if (stepsLocked) return;
    setDef((d) => {
      const next = fn(d);
      return { ...next, edges: sanitizeEdges(next.nodes, next.edges) };
    });
    setDirty(true);
  };
  /** `intoLoopEnd`: the slot below a loop's last block, inside that loop. */
  const insertAt = (index: number, type: NodeType, intoLoopEnd?: string) => {
    const node = blankNode(type);
    // A new loop starts with a one-day Wait inside it: every round needs one.
    const body = type === "loop" ? [{ ...blankNode("delay"), label: "Wait 1 day" }] : undefined;
    mutateDef((d) => insertBlock(d, index, node, { body, intoLoopEnd }));
    setSelectedId(node.id);
  };
  const move = (id: string, dir: -1 | 1) => mutateDef((d) => moveBlock(d, id, dir));
  const remove = (id: string) => {
    mutateDef((d) => removeBlock(d, id));
    if (selectedId === id) setSelectedId(null);
  };

  const invoke = async (body: Record<string, unknown>) => {
    const { data, error } = await supabase.functions.invoke("workflow-definitions", {
      body: { business_id: businessId, ...body },
    });
    if (error) throw new Error(await readEdgeError(error));
    if (data && data.success === false) {
      const detail = Array.isArray(data.issues) && data.issues.length
        ? `: ${data.issues.map((i: { message: string }) => i.message).join("; ")}`
        : "";
      throw new Error((data.error ?? "request failed") + detail);
    }
    return data;
  };

  // Preview one Decision node against a sample from the business's own data.
  // Nothing is saved or sent. It's the "test step" for AI blocks.
  const previewDecision = async (nodeId: string) => {
    setBusy("preview");
    setPreview(null);
    try {
      const data = await invoke({
        action: "preview_decision",
        definition: { ...def, name, description },
        node_id: nodeId,
      });
      setPreview({ nodeId, output: data.output ?? {}, sample: data.sample ?? {} });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Preview failed — try again shortly.");
    } finally {
      setBusy(null);
    }
  };

  // NL → draft: the edge fn compiles the description into a sanitized graph;
  // it lands on the canvas as an ordinary UNSAVED draft the owner reviews.
  const draftWithAI = async () => {
    if (stepsLocked) return;
    const described = aiDescription.trim();
    if (!described) {
      toast.error("Describe the workflow first — e.g. \"when someone no-shows, wait a day, then send a friendly nudge\".");
      return;
    }
    setBusy("draft");
    try {
      const data = await invoke({ action: "draft_from_description", description: described });
      const drafted = data.definition as WorkflowDefinition;
      // Keep this row's identity and its Knowledge. Only the graph, name and
      // description land.
      setDef({ ...drafted, id: def.id, key: def.key, knowledge: def.knowledge });
      setName(drafted.name);
      setDescription(drafted.description ?? "");
      setDirty(true);
      setSelectedId(null);
      toast.success("Draft ready — Dibs also filled in the title and description. Review everything, then save or publish.");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't draft the workflow");
    } finally {
      setBusy(null);
    }
  };

  const saveDraft = async (): Promise<string | null> => {
    setBusy("save");
    try {
      const data = await invoke({
        action: "save_draft",
        id: isNew ? undefined : workflowId,
        name,
        description,
        definition: { ...def, name, description },
      });
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ["workflow-definitions", businessId] });
      queryClient.invalidateQueries({ queryKey: ["workflow-definition", workflowId] });
      toast.success("Draft saved");
      if (isNew && data.workflow?.id) onSavedNew(data.workflow.id);
      return data.workflow?.id ?? workflowId;
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't save the draft");
      return null;
    } finally {
      setBusy(null);
    }
  };

  const publish = async () => {
    if (issues.length > 0) {
      toast.error("Fix the issues listed below before publishing.");
      return;
    }
    setBusy("publish");
    try {
      const id = await (async () => {
        // Persist the latest edits first, then publish the saved row.
        const savedId = dirty || isNew ? await saveDraft() : workflowId;
        if (!savedId) throw new Error("save before publish failed");
        return savedId;
      })();
      const published = await invoke({ action: "publish", id });
      setStatus("published");
      queryClient.invalidateQueries({ queryKey: ["workflow-definitions", businessId] });
      queryClient.invalidateQueries({ queryKey: ["automation-settings", businessId] });
      toast.success("Workflow published — turn it on from the Workflows screen when you're ready.");
      // Non-blocking overlap lint from the backend ("another workflow already
      // fires on this trigger"). Surfaced, never gating the publish.
      for (const w of (published?.warnings as string[] | undefined) ?? []) {
        toast.warning(w, { duration: 12_000 });
      }
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't publish");
    } finally {
      setBusy(null);
    }
  };

  const runDryRun = async (
    scenarioOverride?: NonNullable<DryRunScenario["conditionOutcomes"]>,
    loopRoundsOverride?: NonNullable<DryRunScenario["loopRounds"]>,
  ) => {
    setBusy("dry_run");
    try {
      const savedId = dirty || isNew ? await saveDraft() : workflowId;
      if (!savedId) throw new Error("couldn't save the draft first");
      const defaultScenario = Object.fromEntries(
        def.nodes
          .filter((node) => node.type === "condition")
          .map((node) => [node.id, "continue" as const]),
      );
      const scenario = scenarioOverride ??
        (Object.keys(testScenario).length ? testScenario : defaultScenario);
      setTestScenario(scenario);
      const data = await invoke({
        action: "dry_run",
        id: savedId,
        scenario: { conditionOutcomes: scenario, loopRounds: loopRoundsOverride ?? testLoopRounds },
      });
      setDryRun(data.timeline as DryRunStep[]);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Dry run failed");
    } finally {
      setBusy(null);
    }
  };

  // One block on the canvas, with the insert slot below it. Inside a loop,
  // the slot under the loop's last block adds to the loop, and offers no
  // nested loop.
  const renderBlock = (node: WorkflowNode, i: number, inLoop: LoopSpan | null = null) => {
    const s = BLOCK_STYLES[node.type];
    const NodeIcon = s.icon;
    const step = displaySteps[i];
    const isSelected = node.id === selectedId;
    const usesAI = node.type === "decision" || node.parameters.aiAdjustable === true;

    if (node.type === "condition") {
      const conditionRules = normalizeExceptions(node.parameters.exceptions)
        .map((rule) => rule.label)
        .filter(Boolean);
      const skipEdge = def.edges?.find((edge) => edge.from === node.id && edge.on === "skip");
      const skipTarget = skipEdge && skipEdge.to !== "end"
        ? def.nodes.find((candidate) => candidate.id === skipEdge.to)
        : undefined;
      const elseOutcome = skipTarget
        ? `Continues at “${skipTarget.label ?? BLOCK_STYLES[skipTarget.type].label}”`
        : "Workflow ends";
      const cardClass = `group relative rounded-2xl border bg-card shadow-sm transition-colors ${
        isSelected ? "border-primary ring-1 ring-primary" : "border-border hover:border-primary/40"
      }`;

      return (
        <div key={node.id} className="flex w-full gap-6">
          <div className="flex w-[384px] flex-col items-stretch">
            <div
              role="button"
              tabIndex={0}
              onClick={() => setSelectedId(node.id)}
              onKeyDown={(e) => { if (e.key === "Enter") setSelectedId(node.id); }}
              className={cardClass}
            >
              <NodeHandle position="top" />
              <div className="flex items-center justify-between gap-2 px-4 py-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${s.chip}`}>
                    <NodeIcon className="h-4 w-4" />
                  </span>
                  <p className="truncate text-sm font-semibold leading-snug text-foreground">If</p>
                </div>
                <div className={`${stepsLocked ? "hidden" : "flex"} items-center gap-1 transition-opacity ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"}`}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); move(node.id, -1); }} className="rounded p-1 text-muted-foreground/60 hover:bg-muted hover:text-foreground" aria-label="Move up">
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); move(node.id, 1); }} className="rounded p-1 text-muted-foreground/60 hover:bg-muted hover:text-foreground" aria-label="Move down">
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); remove(node.id); }} className="rounded p-1 text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive" aria-label="Delete block">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="border-t border-border/70" />
              <div className="px-4 py-3">
                <div className="flex items-baseline gap-2">
                  <span className="w-[68px] shrink-0 text-xs text-muted-foreground">Fulfils</span>
                  {conditionRules.length > 0 ? (
                    <div className="flex min-w-0 flex-col gap-1.5">
                      {conditionRules.map((rule, ruleIndex) => (
                        <div key={ruleIndex} className="flex flex-col gap-1.5">
                          <span className="w-fit max-w-full whitespace-normal rounded-md border border-border bg-muted/60 px-1.5 py-0.5 text-left text-xs font-medium text-foreground">
                            {rule}
                          </span>
                          {ruleIndex < conditionRules.length - 1 && (
                            <span className="text-xs font-semibold text-foreground">AND</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">No rules configured</span>
                  )}
                </div>
              </div>
              <NodeHandle position="bottom" />
              <span className="absolute right-[-5px] top-[28px] z-10 h-2.5 w-2.5 -translate-y-1/2 rounded-full border border-border bg-background" />
            </div>
            <Connector onInsert={stepsLocked ? undefined : (type) => insertAt(i + 1, type, inLoop && i === inLoop.endIndex ? inLoop.loopId : undefined)} allowLoop={!inLoop} />
          </div>

          <div className="relative flex w-[384px] flex-col items-stretch">
            <div className="absolute left-[-24px] top-[28px] h-px w-6 bg-border" />
            <div
              role="button"
              tabIndex={0}
              onClick={() => setSelectedId(node.id)}
              onKeyDown={(e) => { if (e.key === "Enter") setSelectedId(node.id); }}
              className={cardClass}
            >
              <NodeHandle position="top" />
              <div className="flex items-center gap-2.5 px-4 py-3">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${s.chip}`}>
                  <NodeIcon className="h-4 w-4" />
                </span>
                <p className="truncate text-sm font-semibold leading-snug text-foreground">Else</p>
              </div>
              <NodeHandle position="bottom" />
              <span className="absolute left-[-5px] top-[28px] z-10 h-2.5 w-2.5 -translate-y-1/2 rounded-full border border-border bg-background" />
            </div>
            <Connector />
            <FlowCap label={elseOutcome} />
          </div>
        </div>
      );
    }

    return (
      <div key={node.id} className="flex w-[384px] flex-col items-stretch">
        <div
          role="button"
          tabIndex={0}
          onClick={() => setSelectedId(node.id)}
          onKeyDown={(e) => { if (e.key === "Enter") setSelectedId(node.id); }}
          className={`group relative rounded-2xl border bg-card shadow-sm transition-colors ${
            isSelected ? "border-primary ring-1 ring-primary" : usesAI ? "border-primary" : "border-border hover:border-primary/40"
          }`}
        >
          <div className="flex items-center justify-between gap-2 px-4 py-3">
            <div className="flex min-w-0 items-center gap-2.5">
              <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${s.chip}`}>
                <NodeIcon className="h-4 w-4" />
              </span>
              <p className="truncate text-sm font-semibold leading-snug text-foreground">
                {node.label ?? s.label}
              </p>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {usesAI && (
                <span className="inline-flex shrink-0 items-center gap-1 text-[10px] font-medium text-primary">
                  <Sparkles className="h-3 w-3" />
                  Powered by Dibs AI
                </span>
              )}
              <div className={`${stepsLocked ? "hidden" : "flex"} items-center gap-1 transition-opacity ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"}`}>
              {node.type !== "trigger" && (
                <>
                  <button type="button" onClick={(e) => { e.stopPropagation(); move(node.id, -1); }} className="rounded p-1 text-muted-foreground/60 hover:bg-muted hover:text-foreground" aria-label="Move up">
                    <ArrowUp className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); move(node.id, 1); }} className="rounded p-1 text-muted-foreground/60 hover:bg-muted hover:text-foreground" aria-label="Move down">
                    <ArrowDown className="h-3.5 w-3.5" />
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); remove(node.id); }} className="rounded p-1 text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive" aria-label="Delete block">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              </div>
            </div>
          </div>
          {(step?.params?.length || step?.exceptions?.length || step?.description || step?.requiresApproval) && (
            <>
              <div className="border-t border-border/70" />
              <div className="space-y-2.5 px-4 py-3">
                {step?.params?.map((p, pi) => (
                  <div key={pi} className="flex min-w-0 items-start gap-2">
                    <span className="w-[68px] shrink-0 pt-1 text-xs text-muted-foreground">{p.label}</span>
                    <span className="flex min-w-0 flex-1 flex-wrap gap-1">
                      {p.values.map((v, vi) => {
                        const isLongValue = v.length > 28;
                        return (
                          <span
                            key={vi}
                            className={isLongValue
                              ? "min-w-0 max-w-full whitespace-normal rounded-lg bg-muted/45 px-2 py-1 text-[11px] font-medium leading-relaxed text-foreground [overflow-wrap:anywhere]"
                              : "shrink-0 whitespace-nowrap rounded-md border border-border bg-muted/60 px-1.5 py-0.5 text-xs font-medium text-foreground"}
                          >
                            {v}
                          </span>
                        );
                      })}
                    </span>
                  </div>
                ))}
                {step?.exceptions?.length ? (
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">Skip if</span>
                    <ul className="mt-1 space-y-0.5">
                      {step.exceptions.map((ex, xi) => (
                        <li key={xi} className="text-xs text-muted-foreground">• {ex}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {step?.description && (
                  <p className="text-[13px] leading-relaxed text-muted-foreground">{step.description}</p>
                )}
                {step?.requiresApproval && (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-700">
                    <ShieldCheck className="h-3.5 w-3.5" />
                    Waits for your approval
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        <Connector onInsert={stepsLocked ? undefined : (type) => insertAt(i + 1, type, inLoop && i === inLoop.endIndex ? inLoop.loopId : undefined)} allowLoop={!inLoop} />
      </div>
    );
  };

  // A loop: its card, the blocks it repeats in a tinted "Each round" frame,
  // and the slot after the frame, which adds a block after the loop.
  const renderLoop = (node: WorkflowNode, i: number, span: LoopSpan | undefined) => {
    const s = BLOCK_STYLES.loop;
    const LoopIcon = s.icon;
    const isSelected = node.id === selectedId;
    const label = node.label || s.label;
    const rules = normalizeExceptions(node.parameters.stopWhen).map((rule) => rule.label).filter(Boolean);
    const rounds = Number(node.parameters.maxIterations ?? 0);
    const inner = span ? def.nodes.slice(span.startIndex, span.endIndex + 1) : [];
    return (
      <div key={node.id} className="flex flex-col items-start">
        <div className="flex w-[384px] flex-col items-stretch">
          <div
            role="button"
            tabIndex={0}
            onClick={() => setSelectedId(node.id)}
            onKeyDown={(e) => { if (e.key === "Enter") setSelectedId(node.id); }}
            className={`group relative rounded-2xl border bg-card shadow-sm transition-colors ${
              isSelected ? "border-primary ring-1 ring-primary" : "border-border hover:border-primary/40"
            }`}
          >
            <NodeHandle position="top" />
            <div className="flex items-center justify-between gap-2 px-4 py-3">
              <div className="flex min-w-0 items-center gap-2.5">
                <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border ${s.chip}`}>
                  <LoopIcon className="h-4 w-4" />
                </span>
                <p className="truncate text-sm font-semibold leading-snug text-foreground">{label}</p>
              </div>
              <div className={`${stepsLocked ? "hidden" : "flex"} items-center gap-1 transition-opacity ${isSelected ? "opacity-100" : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"}`}>
                <button type="button" onClick={(e) => { e.stopPropagation(); move(node.id, -1); }} className="rounded p-1 text-muted-foreground/60 hover:bg-muted hover:text-foreground" aria-label="Move loop up">
                  <ArrowUp className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); move(node.id, 1); }} className="rounded p-1 text-muted-foreground/60 hover:bg-muted hover:text-foreground" aria-label="Move loop down">
                  <ArrowDown className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={(e) => { e.stopPropagation(); remove(node.id); }} className="rounded p-1 text-muted-foreground/60 hover:bg-destructive/10 hover:text-destructive" aria-label="Delete loop (keeps the blocks inside it)">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <div className="border-t border-border/70" />
            <div className="space-y-2.5 px-4 py-3">
              <div className="flex items-baseline gap-2">
                <span className="w-[68px] shrink-0 text-xs text-muted-foreground">Stop when</span>
                {rules.length > 0 ? (
                  <div className="flex min-w-0 flex-col gap-1.5">
                    {rules.map((rule, ruleIndex) => (
                      <div key={ruleIndex} className="flex flex-col gap-1.5">
                        <span className="w-fit max-w-full whitespace-normal rounded-md border border-border bg-muted/60 px-1.5 py-0.5 text-left text-xs font-medium text-foreground">
                          {rule}
                        </span>
                        {ruleIndex < rules.length - 1 && (
                          <span className="text-xs font-semibold text-foreground">OR</span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">No stop rules yet</span>
                )}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="w-[68px] shrink-0 text-xs text-muted-foreground">Max rounds</span>
                <span className="rounded-md border border-border bg-muted/60 px-1.5 py-0.5 text-xs font-medium text-foreground">
                  {rounds || "—"}
                </span>
              </div>
              {node.description && (
                <p className="text-[13px] leading-relaxed text-muted-foreground">{node.description}</p>
              )}
            </div>
            <NodeHandle position="bottom" />
          </div>
          <Connector onInsert={stepsLocked ? undefined : (type) => insertAt(i + 1, type, span ? undefined : node.id)} allowLoop={false} />
        </div>
        <div className="-mx-[13px] w-fit rounded-2xl border border-dashed border-blue-300 bg-blue-50/40 px-3 pb-3 pt-2">
          <p className="pb-2 text-[10px] font-semibold uppercase tracking-wide text-blue-700">
            Each round
          </p>
          {span ? (
            inner.map((child, offset) => renderBlock(child, span.startIndex + offset, span))
          ) : (
            <p className="w-[384px] rounded-xl border border-dashed border-border bg-card/70 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
              No blocks inside yet. Use the + above to add the blocks this loop repeats.
            </p>
          )}
          <div className="flex w-[384px] items-center justify-center gap-1.5 pt-1 text-[11px] font-medium text-blue-700">
            <Repeat className="h-3.5 w-3.5" />
            Back to “{label}”{rounds > 0 ? ` · up to ${rounds} round${rounds === 1 ? "" : "s"}` : ""}
          </div>
        </div>
        <div className="flex w-[384px] flex-col items-stretch">
          <Connector onInsert={stepsLocked ? undefined : (type) => insertAt((span ? span.endIndex : i) + 1, type)} />
        </div>
      </div>
    );
  };

  /** Content for a step: fixed text, or bound to a Decision's output. Used by
   *  the customer message steps and by Save to customer profile. */
  const renderMessageBinding = (
    node: WorkflowNode,
    opts: { label: string; placeholder: string; reviewLinkToken: boolean },
  ) => (
    <div className="space-y-1.5">
      <Label className="text-sm font-normal text-muted-foreground">
        {opts.label}
      </Label>
      {(() => {
        const binding = node.parameters.message as unknown as Binding | undefined;
        const decisions = decisionsBefore(node.id);
        const isAI = binding?.source === "decision";
        return (
          <>
            <Select
              value={isAI ? "ai" : "static"}
              onValueChange={(v) => {
                if (v === "ai") {
                  const target = decisions[decisions.length - 1];
                  if (!target) {
                    toast.error("Add a Decision block above this one first — that's where the AI writes the message.");
                    return;
                  }
                  const field = stringOutputs(target)[0]?.key ?? "message";
                  setParam(node.id, "message", { source: "decision", nodeId: target.id, field });
                } else {
                  setParam(node.id, "message", { source: "static", value: "" });
                }
              }}
            >
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="static">Fixed text (you write it)</SelectItem>
                <SelectItem value="ai">AI-written (from a Decision block)</SelectItem>
              </SelectContent>
            </Select>
            {isAI ? (
              (() => {
                const boundId = (binding as { nodeId?: string })?.nodeId ?? "";
                const target = decisions.find((dn) => dn.id === boundId);
                const fields = stringOutputs(target);
                const boundField = (binding as { field?: string })?.field ?? "message";
                return (
                  <>
                    {decisions.length > 1 ? (
                      <Select
                        value={boundId}
                        onValueChange={(v) => {
                          const next = decisions.find((dn) => dn.id === v);
                          const field = stringOutputs(next)[0]?.key ?? "message";
                          setParam(node.id, "message", { source: "decision", nodeId: v, field });
                        }}
                      >
                        <SelectTrigger className="h-9"><SelectValue placeholder="Pick the Decision" /></SelectTrigger>
                        <SelectContent>
                          {decisions.map((dn) => (
                            <SelectItem key={dn.id} value={dn.id}>{dn.label ?? dn.id}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[11px] text-muted-foreground">
                        Uses what "{decisions[0]?.label ?? "the Decision block"}" writes, above.
                      </p>
                    )}
                    {fields.length > 1 && (
                      <Select
                        value={boundField}
                        onValueChange={(v) =>
                          setParam(node.id, "message", { source: "decision", nodeId: boundId, field: v })}
                      >
                        <SelectTrigger className="h-9"><SelectValue placeholder="Which output" /></SelectTrigger>
                        <SelectContent>
                          {fields.map((f) => (
                            <SelectItem key={f.key} value={f.key}>
                              <span className="block text-sm">{f.key}</span>
                              {f.description && (
                                <SelectItemDescription>{f.description}</SelectItemDescription>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </>
                );
              })()
            ) : (
              <>
                <Textarea
                  value={String((binding as { value?: unknown })?.value ?? "")}
                  onChange={(e) => setParam(node.id, "message", { source: "static", value: e.target.value })}
                  placeholder={opts.placeholder}
                  rows={4}
                  className="resize-none text-sm"
                />
                <p className="text-[11px] text-muted-foreground">
                  You can use {"{{customer_name}}"}, {"{{listing_name}}"}, {"{{booking_date}}"} and {"{{booking_time}}"}.
                  {opts.reviewLinkToken && <>{" "}{"{{google_review_link}}"} adds your Google review link from Connections.</>}
                </p>
              </>
            )}
          </>
        );
      })()}
    </div>
  );

  if (!isNew && isLoading) {
    return <div className="p-8 text-sm text-muted-foreground">Loading workflow…</div>;
  }

  if (isNew && stepsLocked) {
    return (
      <PlanFeatureGate feature="custom_workflows" featureName="building your own workflows" variant="page">
        <div aria-hidden="true" />
      </PlanFeatureGate>
    );
  }

  const fmtAt = (mins: number) => {
    if (mins === 0) return "at trigger";
    if (mins % (24 * 60) === 0) return `+${mins / (24 * 60)}d`;
    if (mins % 60 === 0) return `+${mins / 60}h`;
    return `+${mins}m`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="min-w-0 flex-1">
          <Input
            value={name}
            onChange={(e) => { setName(e.target.value); setDirty(true); }}
            className="h-9 max-w-md text-base font-semibold"
            aria-label="Workflow name"
          />
        </div>
        <Badge variant="outline" className={status === "published"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700 text-[10px]"
          : "border-border bg-muted text-muted-foreground text-[10px]"}>
          {status === "published" ? "Published" : "Draft"}
        </Badge>
        {isComplaintWorkflow && onNavigateToSection && (
          <Button variant="outline" size="sm" onClick={() => onNavigateToSection("crisis", { view: "settings" })} className="gap-1.5">
            Complaint sources
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => runDryRun()} disabled={busy !== null} className="gap-1.5">
          {busy === "dry_run" ? "Testing…" : "Test"}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={saveDraft}
          disabled={busy !== null || (!dirty && !isNew)}
          className="gap-1.5"
        >
          {busy === "save" ? "Saving…" : "Save draft"}
        </Button>
        <Button size="sm" onClick={publish} disabled={busy !== null || issues.length > 0} className="gap-1.5">
          {busy === "publish" ? "Publishing…" : "Publish"}
        </Button>
      </div>

      <Textarea
        value={description}
        onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
        placeholder="What does this workflow do? Shown on the Workflows screen."
        className="max-w-2xl resize-none text-sm"
        rows={2}
      />

      {issues.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <p className="text-xs font-semibold text-amber-900">Before you can publish:</p>
          <ul className="mt-1 space-y-0.5">
            {issues.map((issue, i) => (
              <li key={i} className="text-xs text-amber-800">• {issue.message}</li>
            ))}
          </ul>
        </div>
      )}

      {stepsLocked && (
        <div className="flex flex-col gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-2.5">
            <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <p className="text-sm text-foreground">
              Your plan includes Dibs templates with your own wording: you can change titles and messages.
              Upgrade to Growth to change the steps or build your own workflows.
            </p>
          </div>
          {onNavigateToSection && (
            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => onNavigateToSection("settings", { settings: "billing" })}
            >
              View plans
            </Button>
          )}
        </div>
      )}

      {/* NL → draft — only while the canvas is still blank; disappears the
          moment a graph (drafted or hand-built) exists. */}
      {isNew && def.nodes.length <= 1 && (
        <div className="rounded-xl border border-primary/30 bg-primary/5 p-4">
          <div className="mb-2 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <p className="text-sm font-semibold text-foreground">Describe it — Dibs drafts the blocks</p>
          </div>
          <Textarea
            value={aiDescription}
            onChange={(e) => setAiDescription(e.target.value)}
            placeholder='e.g. "When someone no-shows, wait a day, then send a friendly personalised nudge — unless they already replied."'
            rows={2}
            maxLength={1000}
            className="resize-none bg-background text-sm"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-[11px] text-muted-foreground">
              Dibs also suggests the title and description. You review everything before it is saved — nothing runs
              until you publish and switch it on.
            </p>
            <Button size="sm" onClick={draftWithAI} disabled={busy !== null} className="gap-1.5 shrink-0">
              <Sparkles className="h-3.5 w-3.5" />
              {busy === "draft" ? "Drafting…" : "Draft it for me"}
            </Button>
          </div>
        </div>
      )}

      {/* Match the read-only Detailed view's block legend and visual language. */}
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card px-3 py-2.5">
        <span className="mr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/70">
          Blocks
        </span>
        {(["trigger", "condition", "decision", "execution", "delay"] as NodeType[]).map((type) => {
          const style = BLOCK_STYLES[type];
          const Icon = style.icon;
          return (
            <span
              key={type}
              title={style.blurb}
              className="inline-flex cursor-default items-center gap-1.5 rounded-lg border border-border bg-muted/30 px-2 py-1 text-xs font-medium text-foreground"
            >
              <span className={`flex h-5 w-5 items-center justify-center rounded-md border ${style.chip}`}>
                <Icon className="h-3 w-3" />
              </span>
              {style.label}
            </span>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Canvas */}
        <div
          className="rounded-xl border border-border bg-muted/10 p-6 overflow-x-auto min-h-[480px]"
          style={{
            backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "16px 16px",
          }}
        >
          <div
            className={`mx-auto flex flex-col items-start ${
              def.nodes.some((node) => node.type === "condition") ? "w-[792px]" : "w-[384px]"
            }`}
          >
            <div className="flex w-[384px] flex-col items-stretch">
              <FlowCap label="Workflow starts" />
              <Connector />
            </div>
            {def.nodes.map((node, i) => {
              // Blocks inside a loop are drawn by that loop, in its frame.
              if (enclosingLoop(def, i)) return null;
              if (node.type === "loop") return renderLoop(node, i, spans.find((span) => span.loopId === node.id));
              return renderBlock(node, i);
            })}
            <div className="flex w-[384px] flex-col items-stretch">
              <FlowCap label="Workflow ends" />
            </div>
          </div>
        </div>

        {/* Config panel */}
        <div className="rounded-xl border border-border bg-card">
          {!selected ? (
            <div className="divide-y divide-border">
              <div className="p-6 text-sm text-muted-foreground">
                Select a block to configure it, or use the <Plus className="inline h-3.5 w-3.5" /> buttons on the canvas to add one.
                <p className="mt-3 text-xs leading-relaxed">
                  The flow you draw is fixed — the AI can only adapt <span className="font-medium text-foreground">when</span> things
                  happen (on Waits you allow) and <span className="font-medium text-foreground">what it writes</span> (Decision blocks),
                  always inside the bounds you set here.
                </p>
              </div>
              <div className="p-6">
                <WorkflowKnowledgePanel
                  businessId={businessId}
                  value={def.knowledge}
                  onChange={(knowledge) => {
                    setDef((d) => ({ ...d, knowledge }));
                    setDirty(true);
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4 p-4">
              <div className="flex items-center justify-between">
                <span className="inline-flex items-center gap-2 text-base font-heading font-semibold text-foreground">
                  {(() => { const Icon = BLOCK_STYLES[selected.type].icon; return <Icon className="h-4 w-4" />; })()}
                  {BLOCK_STYLES[selected.type].label} settings
                </span>
                <button type="button" onClick={() => setSelectedId(null)} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close settings">
                  <X className="h-4 w-4" />
                </button>
              </div>

              <div className="space-y-1.5">
                <Label className="text-sm font-normal text-muted-foreground">Block title</Label>
                <Input
                  value={selected.label ?? ""}
                  onChange={(e) => updateNode(selected.id, (n) => ({ ...n, label: e.target.value }))}
                  className="h-9"
                />
              </div>

              {/* Steps with no wording are read-only on plans without custom workflows. */}
              <fieldset
                disabled={stepsLocked && selected.type !== "execution" && selected.type !== "decision"}
                className="min-w-0 space-y-4"
              >
              {/* ── Trigger ── */}
              {selected.type === "trigger" && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-muted-foreground">When</Label>
                    <Select
                      value={String(selected.parameters.event ?? "")}
                      onValueChange={(v) => {
                        const previousEvent = String(selected.parameters.event ?? "");
                        if (isUntouchedTriggerTitle(selected.label, previousEvent)) {
                          const nextTitle = TRIGGER_EVENT_LABELS[v as TriggerEvent];
                          if (nextTitle) {
                            updateNode(selected.id, (n) => ({ ...n, label: nextTitle }));
                          }
                        }
                        setParam(selected.id, "event", v);
                        if (v !== "booking_upcoming") setParam(selected.id, "hoursBefore", undefined);
                        else setParam(selected.id, "hoursBefore", 48);
                        if (v !== "session_finished") {
                          setParam(selected.id, "hoursAfter", undefined);
                          setParam(selected.id, "firstSessionOnly", undefined);
                        } else {
                          setParam(selected.id, "hoursAfter", 1);
                        }
                        // Filters only carry over to events they apply to.
                        if (v !== "booking_cancelled") setParam(selected.id, "cancelledWithinHours", undefined);
                        if (!BOOKING_TRIGGER_EVENTS.includes(v as TriggerEvent)) {
                          setParam(selected.id, "listingId", undefined);
                          setParam(selected.id, "listingName", undefined);
                        }
                        if (v !== "form_submitted") {
                          setParam(selected.id, "formId", undefined);
                          setParam(selected.id, "formName", undefined);
                        }
                        if (v !== "message_received") {
                          setParam(selected.id, "messageChannel", undefined);
                          setParam(selected.id, "newSenderOnly", undefined);
                        }
                      }}
                    >
                      <SelectTrigger className="h-9">
                        {/* Explicit children: the closed control shows only the
                            label, not the item's two-line description. */}
                        <SelectValue placeholder="Pick an event">
                          {selected.parameters.event
                            ? TRIGGER_EVENT_LABELS[String(selected.parameters.event) as TriggerEvent]
                            : undefined}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {TRIGGER_EVENT_GROUPS.map((group) => {
                          const events = group.events.filter((ev) => SUPPORTED_TRIGGER_EVENTS.includes(ev));
                          if (events.length === 0) return null;
                          return (
                            <SelectGroup key={group.label}>
                              <SelectLabel>{group.label}</SelectLabel>
                              {events.map((ev) => (
                                <SelectItem key={ev} value={ev}>
                                  <span className="block">{TRIGGER_EVENT_LABELS[ev]}</span>
                                  <SelectItemDescription>
                                    {TRIGGER_EVENT_DESCRIPTIONS[ev]}
                                  </SelectItemDescription>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  {selected.parameters.event === "booking_upcoming" && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-muted-foreground">How long before the booking (hours)</Label>
                      <Input
                        type="number"
                        min={2}
                        max={168}
                        value={Number(selected.parameters.hoursBefore ?? 48)}
                        onChange={(e) => setParam(selected.id, "hoursBefore", Number(e.target.value))}
                        className="h-9"
                      />
                    </div>
                  )}
                  {selected.parameters.event === "session_finished" && (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-sm font-normal text-muted-foreground">How long after it ends (hours)</Label>
                        <Input
                          type="number"
                          min={0}
                          max={MAX_SESSION_FINISHED_HOURS}
                          value={Number(selected.parameters.hoursAfter ?? 1)}
                          onChange={(e) => setParam(selected.id, "hoursAfter", Number(e.target.value))}
                          className="h-9"
                        />
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <Label className="text-sm font-normal text-foreground">First session only</Label>
                          <p className="text-[11px] text-muted-foreground">
                            Only customers whose first session with you this was. Cancelled and no-show bookings don't count.
                          </p>
                        </div>
                        <Switch
                          checked={selected.parameters.firstSessionOnly === true}
                          onCheckedChange={(on) => setParam(selected.id, "firstSessionOnly", on ? true : undefined)}
                          aria-label="First session only"
                        />
                      </div>
                    </>
                  )}
                  {selected.parameters.event === "form_submitted" && (
                    <div className="space-y-1.5">
                      <Label className="text-xs">Which Tally form</Label>
                      <Select
                        value={String(selected.parameters.formId ?? "any")}
                        onValueChange={(v) => {
                          if (v === "any") {
                            setParam(selected.id, "formId", undefined);
                            setParam(selected.id, "formName", undefined);
                          } else {
                            const form = (tallyForms ?? []).find((item) =>
                              item.tally_form_id === v
                            );
                            setParam(selected.id, "formId", v);
                            setParam(selected.id, "formName", form?.form_name);
                          }
                        }}
                      >
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any connected Tally form</SelectItem>
                          {(tallyForms ?? []).map((form) => (
                            <SelectItem
                              key={form.tally_form_id}
                              value={form.tally_form_id}
                            >
                              {form.form_name}{form.is_enabled ? "" : " (paused)"}
                            </SelectItem>
                          ))}
                          {!!selected.parameters.formId &&
                            !(tallyForms ?? []).some((form) =>
                              form.tally_form_id === selected.parameters.formId
                            ) && (
                              <SelectItem value={String(selected.parameters.formId)}>
                                {String(selected.parameters.formName ?? "Unknown Tally form")}
                              </SelectItem>
                            )}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground">
                        Historical imports only fire workflows when that form’s
                        backfill automation switch is explicitly enabled.
                      </p>
                    </div>
                  )}
                  {BOOKING_TRIGGER_EVENTS.includes(String(selected.parameters.event ?? "") as TriggerEvent) && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-muted-foreground">Which service</Label>
                      <Select
                        value={String(selected.parameters.listingId ?? "any")}
                        onValueChange={(v) => {
                          if (v === "any") {
                            setParam(selected.id, "listingId", undefined);
                            setParam(selected.id, "listingName", undefined);
                          } else {
                            setParam(selected.id, "listingId", v);
                            setParam(selected.id, "listingName", (listings ?? []).find((l) => l.listing_id === v)?.name);
                          }
                        }}
                      >
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">Any service</SelectItem>
                          {(listings ?? []).map((l) => (
                            <SelectItem key={l.listing_id} value={l.listing_id}>{l.name}</SelectItem>
                          ))}
                          {/* Keep a stale scope visible (service deleted/renamed since save). */}
                          {!!selected.parameters.listingId &&
                            !(listings ?? []).some((l) => l.listing_id === selected.parameters.listingId) && (
                              <SelectItem value={String(selected.parameters.listingId)}>
                                {String(selected.parameters.listingName ?? "Unknown service")}
                              </SelectItem>
                            )}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {selected.parameters.event === "booking_cancelled" && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-muted-foreground">Only late cancellations</Label>
                      <Select
                        value={selected.parameters.cancelledWithinHours == null ? "any" : String(selected.parameters.cancelledWithinHours)}
                        onValueChange={(v) => setParam(selected.id, "cancelledWithinHours", v === "any" ? undefined : Number(v))}
                      >
                        <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="any">No — any cancellation</SelectItem>
                          {[2, 6, 12, 24, 48, 72].map((h) => (
                            <SelectItem key={h} value={String(h)}>Only within {h} hours of the session</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-muted-foreground">
                        Treat last-minute cancellations differently from ones made well in advance.
                      </p>
                    </div>
                  )}
                  {selected.parameters.event === "message_received" && (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-sm font-normal text-muted-foreground">Which channel</Label>
                        <Select
                          value={String(selected.parameters.messageChannel ?? "any")}
                          onValueChange={(v) =>
                            setParam(selected.id, "messageChannel", v === "any" ? undefined : v)
                          }
                        >
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(CHANNEL_LABELS).map(([value, label]) => (
                              <SelectItem key={value} value={value}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-normal text-muted-foreground">
                            New enquiries only
                          </Label>
                          <p className="text-[11px] text-muted-foreground">
                            Only run when the message comes from a number you have never
                            heard from before. Anyone who has messaged you in the past is
                            ignored, even if they start a brand-new chat.
                          </p>
                        </div>
                        <Switch
                          checked={selected.parameters.newSenderOnly === true}
                          onCheckedChange={(checked) =>
                            setParam(selected.id, "newSenderOnly", checked ? true : undefined)
                          }
                        />
                      </div>
                    </>
                  )}
                </>
              )}

              {/* ── Condition ── */}
              {selected.type === "condition" && (() => {
                const rules = normalizeExceptions(selected.parameters.exceptions);
                const setRules = (next: PredicateGroup[]) => setParam(selected.id, "exceptions", next);
                const selectedIndex = def.nodes.findIndex((x) => x.id === selected.id);
                const ownLoop = enclosingLoop(def, selectedIndex);
                // A skip may leave a loop, but never land inside one it isn't in.
                const downstream = def.nodes.filter((n, i) => {
                  if (i <= selectedIndex || n.type === "trigger") return false;
                  const targetLoop = enclosingLoop(def, i);
                  return !targetLoop || targetLoop.loopId === ownLoop?.loopId;
                });
                const skipTarget = def.edges?.find((e) => e.from === selected.id && e.on === "skip")?.to ?? "end";
                return (
                <div className="space-y-2">
                  <Label className="text-sm font-normal text-muted-foreground">Rules — the workflow leaves this path if any rule matches</Label>
                  <RulesEditor
                    rules={rules}
                    onChange={setRules}
                    aiFields={decisionFieldDefs(def, def.nodes.findIndex((n) => n.id === selected.id))}
                  />
                  <div className="space-y-1.5 pt-1">
                    <Label className="text-sm font-normal text-muted-foreground">If a rule matches</Label>
                    <Select
                      value={skipTarget}
                      onValueChange={(v) => setSkipEdge(selected.id, v === "end" ? null : v)}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue>
                          {skipTarget === "end"
                            ? "End the workflow"
                            : `Skip ahead to "${def.nodes.find((n) => n.id === skipTarget)?.label ?? "block"}"`}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="end">End the workflow</SelectItem>
                        {downstream.map((n) => (
                          <SelectItem key={n.id} value={n.id}>
                            Skip ahead to "{n.label ?? BLOCK_STYLES[n.type].label}"
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] leading-relaxed text-muted-foreground">
                    Checks run when the workflow reaches this block. Every check inside a rule must be true
                    for it to match; the workflow continues only when no rule matches.
                  </p>
                </div>
                );
              })()}

              {/* ── Loop (Repeat until) ── */}
              {selected.type === "loop" && (() => {
                const rules = normalizeExceptions(selected.parameters.stopWhen);
                const span = spans.find((sp) => sp.loopId === selected.id);
                const inner = span ? def.nodes.slice(span.startIndex, span.endIndex + 1) : [];
                const rounds = selected.parameters.maxIterations;
                return (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-sm font-normal text-muted-foreground">Stop when — the loop ends as soon as any rule matches</Label>
                      <RulesEditor
                        rules={rules}
                        onChange={(next) => setParam(selected.id, "stopWhen", next)}
                        aiFields={decisionFieldDefs(def, def.nodes.findIndex((n) => n.id === selected.id))}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-muted-foreground">Max rounds</Label>
                      <Input
                        type="number"
                        min={1}
                        max={MAX_LOOP_ITERATIONS}
                        step={1}
                        value={typeof rounds === "number" ? rounds : ""}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          setParam(
                            selected.id,
                            "maxIterations",
                            e.target.value === "" || !Number.isFinite(value) ? undefined : Math.round(value),
                          );
                        }}
                        className="h-9"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Up to {MAX_LOOP_ITERATIONS}. After the last round the workflow carries on, even if no stop rule matched.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-muted-foreground">Blocks repeated each round</Label>
                      {inner.length > 0 ? (
                        <ol className="space-y-1">
                          {inner.map((n, k) => (
                            <li key={n.id} className="flex items-center gap-2 text-xs">
                              <span className="text-muted-foreground">{k + 1}.</span>
                              <span className="truncate font-medium text-foreground">{n.label || BLOCK_STYLES[n.type].label}</span>
                            </li>
                          ))}
                        </ol>
                      ) : (
                        <p className="text-xs text-muted-foreground">None yet. Add blocks with the + under this loop.</p>
                      )}
                      <div className="flex gap-2 pt-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 flex-1 text-xs"
                          disabled={!canIncludeNextBlock(def, selected.id)}
                          onClick={() => mutateDef((d) => includeNextBlock(d, selected.id))}
                        >
                          Include the next block
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 flex-1 text-xs"
                          disabled={!span}
                          onClick={() => mutateDef((d) => releaseLastBlock(d, selected.id))}
                        >
                          Move the last block out
                        </Button>
                      </div>
                    </div>
                    <p className="text-[11px] leading-relaxed text-muted-foreground">
                      Before every round the stop rules are checked against the customer’s latest activity
                      (“has replied” means since the loop’s last message). Only messages, tags and notifications
                      to you can repeat. A round that messages the customer needs a Wait of at least 24 hours,
                      and every message still passes the usual safety checks: opt-outs, paused outreach and
                      conversations you’ve taken over.
                    </p>
                  </div>
                );
              })()}

              {/* ── Decision ── */}
              {selected.type === "decision" && (() => {
                const contract = (selected.parameters.output as unknown as OutputField[]) ?? [];
                const setContract = (next: OutputField[]) => setParam(selected.id, "output", next);
                // Renaming an output key follows every send bound to it, so a
                // rename never silently breaks a binding.
                const renameField = (fi: number, rawKey: string) => {
                  const key = rawKey.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "").slice(0, 40);
                  const oldKey = contract[fi]?.key;
                  if (!key || key === oldKey || contract.some((f, i) => i !== fi && f.key === key)) return;
                  setContract(contract.map((f, i) => (i === fi ? { ...f, key } : f)));
                  mutate((nodes) =>
                    nodes.map((n) => {
                      const b = n.parameters.message as unknown as Binding | undefined;
                      if (n.type === "execution" && b?.source === "decision" && b.nodeId === selected.id && b.field === oldKey) {
                        return { ...n, parameters: { ...n.parameters, message: { ...b, field: key } as unknown as WorkflowNode["parameters"][string] } };
                      }
                      return n;
                    }));
                };
                return (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-muted-foreground">What should the AI write?</Label>
                    <div className="flex flex-wrap gap-1.5">
                      {GOAL_PRESETS.map((gp) => (
                        <button
                          key={gp.label}
                          type="button"
                          onClick={() => setParam(selected.id, "goal", gp.goal)}
                          className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:border-primary hover:text-primary"
                        >
                          {gp.label}
                        </button>
                      ))}
                    </div>
                    <Textarea
                      value={String(selected.parameters.goal ?? "")}
                      onChange={(e) => setParam(selected.id, "goal", e.target.value)}
                      placeholder="e.g. Write a warm, personal follow-up inviting them back, referencing what they enjoy."
                      rows={3}
                      className="resize-none text-sm"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-muted-foreground">Tone (optional)</Label>
                    <Input
                      value={String(selected.parameters.tone ?? "")}
                      onChange={(e) => setParam(selected.id, "tone", e.target.value || undefined)}
                      placeholder="Friendly, light, no pressure"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-muted-foreground">Must avoid (optional, comma-separated)</Label>
                    <Input
                      value={Array.isArray(selected.parameters.mustAvoid) ? (selected.parameters.mustAvoid as string[]).join(", ") : ""}
                      onChange={(e) => {
                        const list = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                        setParam(selected.id, "mustAvoid", list.length ? list : undefined);
                      }}
                      placeholder="discounts, false urgency"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-muted-foreground">Must include (optional, comma-separated)</Label>
                    <Input
                      value={Array.isArray(selected.parameters.mustInclude) ? (selected.parameters.mustInclude as string[]).join(", ") : ""}
                      onChange={(e) => {
                        const list = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                        setParam(selected.id, "mustInclude", list.length ? list : undefined);
                      }}
                      placeholder="e.g. our booking link"
                      className="h-9"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      Both are enforced after generation — output that breaks them is rewritten once, then dropped
                      (the send is skipped) rather than sent anyway.
                    </p>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-muted-foreground">The AI produces</Label>
                    {contract.map((f, fi) => (
                      <div key={fi} className="flex items-center gap-2">
                        <Input
                          value={f.key}
                          onChange={(e) => renameField(fi, e.target.value)}
                          className="h-9 w-36 text-sm"
                          aria-label="Output name"
                        />
                        <Input
                          value={f.description ?? ""}
                          onChange={(e) => setContract(contract.map((x, i) => (i === fi ? { ...x, description: e.target.value || undefined } : x)))}
                          placeholder="What this text is for"
                          className="h-9 flex-1"
                        />
                        {contract.length > 1 && (
                          <button
                            type="button"
                            onClick={() => setContract(contract.filter((_, i) => i !== fi))}
                            className="rounded p-1 text-muted-foreground/60 hover:text-destructive"
                            aria-label="Remove output"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        const base = "message";
                        let key = base, i = 2;
                        while (contract.some((f) => f.key === key)) key = `${base}_${i++}`;
                        setContract([...contract, { key, type: "string" }]);
                      }}
                      className="text-[11px] font-medium text-primary hover:underline"
                    >
                      + Add another output
                    </button>
                    <p className="text-[11px] text-muted-foreground">
                      One Decision can write several pieces at once — e.g. the message now and a follow-up for later —
                      and each Send picks which one it uses.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-1.5"
                    disabled={busy !== null}
                    onClick={() => previewDecision(selected.id)}
                  >
                    <Sparkles className="h-3.5 w-3.5" />
                    {busy === "preview" ? "Asking the AI…" : "Preview what the AI writes"}
                  </Button>
                  {preview && preview.nodeId === selected.id && (
                    <div className="space-y-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5">
                      <p className="text-[11px] font-medium text-violet-900">
                        Sample for {String(preview.sample.customer_name ?? "a customer")}
                        {preview.sample.listing_name ? ` · ${String(preview.sample.listing_name)}` : ""} — nothing was sent.
                      </p>
                      {Object.entries(preview.output).map(([k, v]) => (
                        <p key={k} className="text-xs leading-relaxed text-violet-900">
                          <span className="font-mono text-[10px] text-violet-700">{k}:</span> {String(v)}
                        </p>
                      ))}
                    </div>
                  )}
                  <p className="rounded-lg bg-violet-50 px-3 py-2 text-[11px] leading-relaxed text-violet-800">
                    The AI writes from the run's real facts — the booking, the customer's history and conversation —
                    but can never invent prices, discounts, or availability. Point a Send block's message at this Decision to use it.
                  </p>
                </>
                );
              })()}

              {/* ── Execution ── */}
              {selected.type === "execution" && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-muted-foreground">Action</Label>
                    <Select
                      disabled={stepsLocked}
                      value={String(selected.parameters.action ?? "send_message")}
                      onValueChange={(v) => {
                        setParam(selected.id, "action", v);
                        if ((v === "send_message" || v === "request_feedback" || v === "schedule_followup") && selected.parameters.recipient == null) {
                          setParam(selected.id, "recipient", "workflow_subject");
                        }
                        if (v === "request_feedback") {
                          if (selected.parameters.feedbackExpiresDays == null) setParam(selected.id, "feedbackExpiresDays", 14);
                          setParam(selected.id, "requiresApproval", undefined);
                        }
                        // Seed the move distance so what's stored matches what
                        // the field shows. Publishing gates on this parameter
                        // being present, so a display-only `?? 7` default would
                        // fail publish while the box plainly read 7.
                        if (v === "reschedule_booking" && selected.parameters.rescheduleByDays == null) {
                          setParam(selected.id, "rescheduleByDays", 7);
                        }
                        if (v === "open_case") {
                          if (selected.parameters.caseCategory == null) setParam(selected.id, "caseCategory", "service");
                          if (selected.parameters.caseSeverity == null) setParam(selected.id, "caseSeverity", "normal");
                          if (selected.parameters.assignedRole == null) setParam(selected.id, "assignedRole", "clinic_manager");
                          if (selected.parameters.acknowledgeWithinMinutes == null) setParam(selected.id, "acknowledgeWithinMinutes", 30);
                          if (selected.parameters.resolveWithinMinutes == null) setParam(selected.id, "resolveWithinMinutes", 24 * 60);
                          if (selected.parameters.pauseOutreach == null) setParam(selected.id, "pauseOutreach", true);
                        }
                        if (v === "pause_outreach" && selected.parameters.holdDays == null) {
                          setParam(selected.id, "holdDays", 30);
                        }
                        if (v === "save_to_profile") {
                          // Team-facing, so no approval and no recipient.
                          setParam(selected.id, "requiresApproval", undefined);
                          setParam(selected.id, "recipient", undefined);
                          if (selected.parameters.message == null) {
                            setParam(selected.id, "message", { source: "static", value: "" });
                          }
                        }
                      }}
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Pick an action">
                          {selected.parameters.action
                            ? ACTION_LABELS[String(selected.parameters.action) as ExecutionAction]
                            : undefined}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {ACTION_GROUPS.map((group) => {
                          const actions = group.actions.filter((a) => SUPPORTED_ACTIONS.includes(a));
                          if (actions.length === 0) return null;
                          return (
                            <SelectGroup key={group.label}>
                              <SelectLabel>{group.label}</SelectLabel>
                              {actions.map((a) => (
                                <SelectItem key={a} value={a}>
                                  <span className="block">{ACTION_LABELS[a]}</span>
                                  <SelectItemDescription>{ACTION_DESCRIPTIONS[a]}</SelectItemDescription>
                                </SelectItem>
                              ))}
                            </SelectGroup>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                  {(selected.parameters.action === "add_tag" ||
                    selected.parameters.action === "remove_tag") && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-muted-foreground">Tag</Label>
                      <Input
                        value={String(selected.parameters.tag ?? "")}
                        onChange={(e) => setParam(selected.id, "tag", e.target.value || undefined)}
                        placeholder="e.g. no-show-risk"
                        className="h-9"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        {selected.parameters.action === "add_tag"
                          ? "Stamped on the customer's profile so you can filter and segment by it."
                          : "Cleared from the customer's profile. Must match the tag exactly — nothing else in Dibs removes tags."}
                      </p>
                    </div>
                  )}
                  {selected.parameters.action === "save_to_profile" && (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-sm font-normal text-muted-foreground">Note title</Label>
                        <Input
                          value={String(selected.parameters.noteTitle ?? "")}
                          onChange={(e) => setParam(selected.id, "noteTitle", e.target.value || undefined)}
                          maxLength={MAX_NOTE_TITLE_CHARS}
                          placeholder="e.g. Feedback after first session"
                          className="h-9"
                        />
                      </div>
                      {renderMessageBinding(selected, {
                        label: "Note",
                        placeholder: "What to note about {{customer_name}}…",
                        reviewLinkToken: false,
                      })}
                      <p className="text-[11px] text-muted-foreground">
                        Saved under Notes on the customer&apos;s profile, where your team and the AI&apos;s customer
                        summary can see it. The customer never does.
                      </p>
                    </>
                  )}
                  {selected.parameters.action === "schedule_followup" && (
                    <>
                      <DurationField
                        minutes={Number(selected.parameters.followupDelayMinutes ?? 24 * 60)}
                        onChange={(mins) => setParam(selected.id, "followupDelayMinutes", mins)}
                        label="Send after"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        The workflow keeps going — the follow-up lands later as a scheduled
                        message you can see and cancel from the Conversations screen.
                      </p>
                    </>
                  )}
                  {selected.parameters.action === "request_feedback" && (
                    <div className="space-y-3 rounded-lg border border-border p-3">
                      <div className="space-y-1.5">
                        <Label className="text-sm font-normal text-muted-foreground">Private link expires after (days)</Label>
                        <Input
                          type="number"
                          min={1}
                          max={30}
                          value={Number(selected.parameters.feedbackExpiresDays ?? 14)}
                          onChange={(e) => setParam(selected.id, "feedbackExpiresDays", Number(e.target.value))}
                          className="h-9"
                        />
                      </div>
                      <p className="text-[11px] leading-relaxed text-muted-foreground">
                        Each customer gets a different one-use link. Their answer stays private.
                        Ratings of 1–2 and any medical, safety or privacy warning open a human-owned
                        case automatically. Dibs never redirects only happy customers to a public review.
                      </p>
                    </div>
                  )}
                  {(selected.parameters.action === "send_message" ||
                    selected.parameters.action === "request_feedback" ||
                    selected.parameters.action === "schedule_followup") && (
                    <>
                      <div className="rounded-lg border border-border bg-muted/40 px-3 py-2.5">
                        <p className="text-xs font-medium text-foreground">Recipient</p>
                        <p className="text-[11px] leading-relaxed text-muted-foreground">
                          The customer this workflow started for. Actions such as filling a
                          waitlist never silently change who later messages go to.
                        </p>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm font-normal text-muted-foreground">Only on</Label>
                        <Select
                          disabled={stepsLocked}
                          value={String(selected.parameters.onChannel ?? "any")}
                          onValueChange={(v) => setParam(selected.id, "onChannel", v === "any" ? undefined : v)}
                        >
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(CHANNEL_LABELS).map(([v, l]) => (
                              <SelectItem key={v} value={v}>{l}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-[11px] text-muted-foreground">
                          Messages always go out on the channel the customer already chats with
                          you on — you can&apos;t pick a destination. This narrows{" "}
                          <em>whether</em> the step runs: leave it on &ldquo;Any channel&rdquo;
                          unless you want it to fire only for one.
                        </p>
                      </div>
                      {renderMessageBinding(selected, {
                        label: selected.parameters.action === "request_feedback" ? "Invitation message" : "Message",
                        placeholder: "Hi {{customer_name}}! Your {{listing_name}} is coming up…",
                        reviewLinkToken: true,
                      })}
                    </>
                  )}
                  {selected.parameters.action === "reschedule_booking" && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-muted-foreground">Move back by (days)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={MAX_RESCHEDULE_DAYS}
                        value={Number(selected.parameters.rescheduleByDays ?? 7)}
                        onChange={(e) => setParam(selected.id, "rescheduleByDays", Number(e.target.value))}
                        className="h-9"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        The booking keeps its time of day and moves this many days later.
                        You can&apos;t pick an exact date here — the workflow doesn&apos;t know one
                        when you write it. Dibs checks the new slot is actually free before
                        moving anything, and bookings paid online are left for you to handle.
                      </p>
                    </div>
                  )}
                  {selected.parameters.action === "open_case" && (
                    <div className="space-y-3 rounded-lg border border-border p-3">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="space-y-1.5">
                          <Label className="text-sm font-normal text-muted-foreground">Category</Label>
                          <Select
                            value={String(selected.parameters.caseCategory ?? "service")}
                            onValueChange={(v) => setParam(selected.id, "caseCategory", v)}
                          >
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="clinical">Clinical concern</SelectItem>
                              <SelectItem value="safety">Safety concern</SelectItem>
                              <SelectItem value="service">Service complaint</SelectItem>
                              <SelectItem value="billing">Billing issue</SelectItem>
                              <SelectItem value="privacy">Privacy concern</SelectItem>
                              <SelectItem value="booking">Booking issue</SelectItem>
                              <SelectItem value="operational">Operational incident</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-sm font-normal text-muted-foreground">Urgency</Label>
                          <Select
                            value={String(selected.parameters.caseSeverity ?? "normal")}
                            onValueChange={(v) => setParam(selected.id, "caseSeverity", v)}
                          >
                            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="emergency">Emergency · 5 min response</SelectItem>
                              <SelectItem value="high">High · 30 min response</SelectItem>
                              <SelectItem value="normal">Normal · 4 hour response</SelectItem>
                              <SelectItem value="low">Low · 1 day response</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-sm font-normal text-muted-foreground">Responsible role</Label>
                        <Select
                          value={String(selected.parameters.assignedRole ?? "clinic_manager")}
                          onValueChange={(v) => setParam(selected.id, "assignedRole", v)}
                        >
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="clinic_manager">Clinic manager</SelectItem>
                            <SelectItem value="clinician">Clinician</SelectItem>
                            <SelectItem value="front_desk">Front desk</SelectItem>
                            <SelectItem value="billing">Billing</SelectItem>
                            <SelectItem value="operations">Operations</SelectItem>
                            <SelectItem value="outlet_manager">Outlet manager</SelectItem>
                            <SelectItem value="security">Security</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <DurationField
                          minutes={Number(selected.parameters.acknowledgeWithinMinutes ?? 30)}
                          onChange={(mins) => setParam(selected.id, "acknowledgeWithinMinutes", mins)}
                          label="Acknowledge within"
                        />
                        <DurationField
                          minutes={Number(selected.parameters.resolveWithinMinutes ?? 24 * 60)}
                          onChange={(mins) => setParam(selected.id, "resolveWithinMinutes", mins)}
                          label="Resolve within"
                        />
                      </div>
                      <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 px-3 py-2.5">
                        <div>
                          <p className="text-xs font-medium">Pause ordinary outreach</p>
                          <p className="text-[11px] text-muted-foreground">Stops sales and follow-up messages while people handle the case.</p>
                        </div>
                        <Switch
                          checked={selected.parameters.pauseOutreach === true}
                          onCheckedChange={(v) => setParam(selected.id, "pauseOutreach", v)}
                        />
                      </div>
                      <p className="text-[11px] leading-relaxed text-muted-foreground">
                        AI may suggest the category and urgency when a chat triggers the case, but a person must acknowledge, respond and resolve it. Clinical and safety cases are never auto-closed.
                      </p>
                    </div>
                  )}
                  {selected.parameters.action === "pause_outreach" && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-muted-foreground">Pause for (days)</Label>
                      <Input
                        type="number"
                        min={1}
                        max={365}
                        value={Number(selected.parameters.holdDays ?? 30)}
                        onChange={(e) => setParam(selected.id, "holdDays", Number(e.target.value))}
                        className="h-9"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        All proactive workflow and campaign messages wait. Booking confirmations and other necessary transaction messages still work.
                      </p>
                    </div>
                  )}
                  {(selected.parameters.action === "send_message" ||
                    selected.parameters.action === "request_feedback") && (
                    <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
                      <div className="pr-3">
                        <p className="text-xs font-medium text-foreground">Email them if they&apos;ve never messaged you</p>
                        <p className="text-[11px] text-muted-foreground">
                          Customers who booked online without chatting have no conversation, so
                          this step normally skips them. With this on, it emails the address on
                          their booking instead. Same opt-out and sending rules apply.
                        </p>
                      </div>
                      <Switch
                        checked={selected.parameters.emailFallback === true}
                        onCheckedChange={(v) => setParam(selected.id, "emailFallback", v || undefined)}
                      />
                    </div>
                  )}
                  {selected.parameters.action === "send_message" && (() => {
                    const auto = selected.parameters.whatsappTemplateAuto === true;
                    const currentId = typeof selected.parameters.whatsappTemplateId === "string"
                      ? selected.parameters.whatsappTemplateId
                      : "";
                    const allTemplates = workflowTemplates ?? [];
                    // Dibs-made templates belong to the wording they were made
                    // from, so only "Automatic" uses them.
                    const templates = allTemplates.filter((t) => !t.managed);
                    const linked = auto ? allTemplates.find((t) => t.id === currentId) : undefined;
                    const missing = !auto && currentId !== "" && !templates.some((t) => t.id === currentId);
                    const aiWritten = (selected.parameters.message as unknown as Binding | undefined)?.source === "decision";
                    return (
                      <div className="space-y-1.5">
                        <Label className="text-sm font-normal text-muted-foreground">
                          WhatsApp template after 24 hours
                        </Label>
                        <Select
                          value={auto ? "auto" : currentId || "none"}
                          onValueChange={(v) => {
                            const template = templates.find((t) => t.id === v);
                            updateNode(selected.id, (n) => {
                              const parameters = { ...n.parameters } as Record<string, unknown>;
                              delete parameters.whatsappTemplateAuto;
                              if (v === "auto") {
                                parameters.whatsappTemplateAuto = true;
                                delete parameters.whatsappTemplateId;
                                delete parameters.whatsappTemplateName;
                              } else if (template) {
                                parameters.whatsappTemplateId = template.id;
                                parameters.whatsappTemplateName = template.provider_template_name;
                              } else if (v === "none") {
                                delete parameters.whatsappTemplateId;
                                delete parameters.whatsappTemplateName;
                              }
                              return { ...n, parameters: parameters as WorkflowNode["parameters"] };
                            });
                          }}
                        >
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="auto">Automatic: Dibs gets this message approved by Meta</SelectItem>
                            <SelectItem value="none">None: skip once the window closes</SelectItem>
                            {templates.map((t) => (
                              <SelectItem key={t.id} value={t.id}>
                                {t.provider_template_name}
                                {t.status === "approved" ? "" : ` (${TEMPLATE_STATUS_LABEL[t.status] ?? t.status})`}
                              </SelectItem>
                            ))}
                            {missing && (
                              <SelectItem value={currentId}>
                                {String(selected.parameters.whatsappTemplateName ?? "Template")} (deleted)
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        {auto && aiWritten && (
                          <p className="text-[11px] text-destructive">
                            Automatic templates need fixed text. Meta only approves fixed wording, and
                            AI-written messages change every time.
                          </p>
                        )}
                        {auto && !aiWritten && (
                          <p className="text-[11px] text-foreground">
                            {linked
                              ? linked.status === "approved"
                                ? "Approved by Meta. Used once the 24-hour window has closed."
                                : linked.status_note ?? TEMPLATE_STATUS_LABEL[linked.status] ?? linked.status
                              : "When you publish, Dibs turns this message into a WhatsApp template and submits it to Meta. Editing the message later makes a new one."}
                          </p>
                        )}
                        <p className="text-[11px] text-muted-foreground">
                          WhatsApp only allows a normal message within 24 hours of the customer&apos;s
                          last message. After that, this step sends a Meta-approved template instead.
                          Without one, it skips.
                        </p>
                      </div>
                    );
                  })()}
                  {(selected.parameters.action === "escalate_conversation" ||
                    selected.parameters.action === "cancel_booking" ||
                    selected.parameters.action === "notify_owner" ||
                    selected.parameters.action === "open_case" ||
                    selected.parameters.action === "pause_outreach") && (
                    <div className="space-y-1.5">
                      <Label className="text-sm font-normal text-muted-foreground">
                        {selected.parameters.action === "notify_owner"
                          ? "What to tell me"
                          : selected.parameters.action === "open_case"
                            ? "Case summary"
                            : "Reason"}
                      </Label>
                      <Textarea
                        value={String(selected.parameters.reason ?? "")}
                        onChange={(e) => setParam(selected.id, "reason", e.target.value || undefined)}
                        placeholder={
                          selected.parameters.action === "notify_owner"
                            ? "e.g. {{customer_name}} cancelled {{listing_name}} at short notice"
                            : selected.parameters.action === "open_case"
                              ? "e.g. {{customer_name}} reported a serious concern after {{listing_name}}"
                            : "e.g. Late cancellation — needs a personal follow-up"
                        }
                        rows={2}
                        className="resize-none text-sm"
                      />
                      <p className="text-[11px] text-muted-foreground">
                        Only you see this — it never goes to the customer. You can use{" "}
                        {"{{customer_name}}"}, {"{{listing_name}}"}, {"{{booking_date}}"} and {"{{booking_time}}"}.
                      </p>
                    </div>
                  )}
                  {(() => {
                    // E6. Say which rails apply to the SELECTED action, rather
                    // than leaving the owner to infer it. The customer-facing
                    // actions go through opt-outs, sending window and quota;
                    // the owner-facing ones deliberately don't.
                    const action = String(selected.parameters.action ?? "send_message");
                    const customerFacing = action === "send_message" || action === "request_feedback" || action === "schedule_followup";
                    const ownerFacing = action === "notify_owner" || action === "escalate_conversation";
                    const caseSafety = action === "open_case" || action === "pause_outreach";
                    if (!customerFacing && !ownerFacing && !caseSafety) return null;
                    return (
                      <div className="rounded-lg border border-border bg-muted/40 px-3 py-2.5">
                        <p className="text-[11px] leading-relaxed text-muted-foreground">
                          {customerFacing ? (
                            <>
                              <span className="font-medium text-foreground">Checked before this goes out:</span>{" "}
                              the customer&apos;s opt-out, any pause you&apos;ve put on their account,
                              your sending window, how recently another automation messaged them,
                              and your monthly engaged-customer allowance. If the booking is cancelled by
                              then, the message is dropped rather than sent.
                            </>
                          ) : caseSafety ? (
                            <>
                              <span className="font-medium text-foreground">This protects the customer while your team takes over</span>
                              {" "}— it never sends a diagnosis, refund, compensation, or resolution by itself. Every change is kept in the case timeline.
                            </>
                          ) : (
                            <>
                              <span className="font-medium text-foreground">This one goes to you, not the customer</span>
                              {" "}— so opt-outs, sending windows and your engaged-customer allowance don&apos;t
                              apply. Workflow notifications are capped at 10 an hour so a busy day
                              can&apos;t flood your inbox.
                            </>
                          )}
                        </p>
                      </div>
                    );
                  })()}
                  {(() => {
                    const forced = ALWAYS_APPROVE_ACTIONS.includes(
                      String(selected.parameters.action ?? "") as ExecutionAction,
                    );
                    const feedbackRequest = selected.parameters.action === "request_feedback";
                    // A profile note is internal and instant: nothing to approve.
                    if (selected.parameters.action === "save_to_profile") return null;
                    return (
                      <div className="flex items-center justify-between rounded-lg border border-border px-3 py-2.5">
                        <div>
                          <p className="text-xs font-medium text-foreground">Wait for my approval</p>
                          <p className="text-[11px] text-muted-foreground">
                            {forced
                              ? "Always on for this action — it won't run until you approve it."
                              : feedbackRequest
                                ? "Review the invitation before publishing. One-use links are created only when the live workflow runs, so this action cannot wait in the approval queue."
                              : "Queues the action until you approve it."}
                          </p>
                        </div>
                        <Switch
                          checked={forced || (!feedbackRequest && selected.parameters.requiresApproval === true)}
                          disabled={forced || feedbackRequest}
                          onCheckedChange={(v) => setParam(selected.id, "requiresApproval", v || undefined)}
                        />
                      </div>
                    );
                  })()}
                </>
              )}

              {/* ── Delay ── */}
              {selected.type === "delay" && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-normal text-muted-foreground">Wait mode</Label>
                    <Select
                      value={String(selected.parameters.mode ?? "duration")}
                      onValueChange={(v) => {
                        setParam(selected.id, "mode", v);
                        if (v === "duration") {
                          setParam(selected.id, "untilEvent", undefined);
                          setParam(selected.id, "timeoutMinutes", undefined);
                          if (selected.parameters.durationMinutes == null) setParam(selected.id, "durationMinutes", 24 * 60);
                        } else if (v === "until_reply") {
                          // AI timing only stretches fixed waits.
                          setParam(selected.id, "untilEvent", undefined);
                          setParam(selected.id, "durationMinutes", undefined);
                          setParam(selected.id, "aiAdjustable", undefined);
                          setParam(selected.id, "maxExtensionMinutes", undefined);
                          setParam(selected.id, "timeoutMinutes", 48 * 60);
                        } else {
                          setParam(selected.id, "untilEvent", "booking_completed");
                          setParam(selected.id, "timeoutMinutes", 48 * 60);
                        }
                      }}
                    >
                      <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="duration">A fixed amount of time</SelectItem>
                        <SelectItem value="until_event">Until something happens</SelectItem>
                        <SelectItem value="until_reply">Until the customer replies</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {selected.parameters.mode === "until_reply" ? (
                    <>
                      <DurationField
                        minutes={Number(selected.parameters.timeoutMinutes ?? 48 * 60)}
                        onChange={(m) => setParam(selected.id, "timeoutMinutes", m)}
                        label="Give up waiting after"
                      />
                      <p className="text-[11px] leading-4 text-muted-foreground">
                        Carries on a few minutes after they reply, so they can finish typing and the AI can answer first.
                        Add a Condition on "Customer has replied" below to handle no reply.
                      </p>
                    </>
                  ) : selected.parameters.mode === "until_event" ? (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-sm font-normal text-muted-foreground">Wait until</Label>
                        <Select
                          value={String(selected.parameters.untilEvent ?? "booking_completed")}
                          onValueChange={(v) => setParam(selected.id, "untilEvent", v)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue>
                              {TRIGGER_EVENT_LABELS[String(selected.parameters.untilEvent ?? "booking_completed") as TriggerEvent]}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {(["booking_completed", "booking_cancelled", "no_show", "booking_created"] as TriggerEvent[]).map((ev) => (
                              <SelectItem key={ev} value={ev}>
                                <span className="block">{TRIGGER_EVENT_LABELS[ev]}</span>
                                <SelectItemDescription>
                                  {TRIGGER_EVENT_DESCRIPTIONS[ev]}
                                </SelectItemDescription>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <DurationField
                        minutes={Number(selected.parameters.timeoutMinutes ?? 48 * 60)}
                        onChange={(m) => setParam(selected.id, "timeoutMinutes", m)}
                        label="Give up waiting after"
                      />
                    </>
                  ) : (
                    <DurationField
                      minutes={Number(selected.parameters.durationMinutes ?? 0)}
                      onChange={(m) => setParam(selected.id, "durationMinutes", m)}
                      label="Wait for"
                    />
                  )}
                  {selected.parameters.mode !== "until_reply" && (
                  <div className="rounded-lg border border-border px-3 py-2.5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="inline-flex items-center gap-1 text-xs font-medium text-foreground">
                          <Sparkles className="h-3 w-3 text-primary" />
                          Let the AI extend this wait
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          e.g. holds off if the customer mentions a family emergency. It can only wait longer — never less.
                        </p>
                      </div>
                      <Switch
                        checked={selected.parameters.aiAdjustable === true}
                        onCheckedChange={(v) => {
                          setParam(selected.id, "aiAdjustable", v || undefined);
                          if (v && selected.parameters.maxExtensionMinutes == null) {
                            setParam(selected.id, "maxExtensionMinutes", 5 * 24 * 60);
                          }
                          if (!v) setParam(selected.id, "maxExtensionMinutes", undefined);
                        }}
                      />
                    </div>
                    {selected.parameters.aiAdjustable === true && (
                      <div className="mt-2.5">
                        <DurationField
                          minutes={Number(selected.parameters.maxExtensionMinutes ?? 5 * 24 * 60)}
                          onChange={(m) => setParam(selected.id, "maxExtensionMinutes", m)}
                          label="At most this much extra"
                        />
                      </div>
                    )}
                  </div>
                  )}
                </>
              )}
              </fieldset>

              <div className="space-y-1.5">
                <Label className="text-sm font-normal text-muted-foreground">Note (optional, shown on the card)</Label>
                <Textarea
                  value={selected.description ?? ""}
                  onChange={(e) => updateNode(selected.id, (n) => ({ ...n, description: e.target.value || undefined }))}
                  rows={2}
                  className="resize-none text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Dry-run timeline */}
      <Dialog open={dryRun !== null} onOpenChange={(o) => !o && setDryRun(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FlaskConical className="h-4 w-4" />
              Scenario Lab — what would happen
            </DialogTitle>
          </DialogHeader>
          {def.nodes.some((node) => node.type === "condition") && (
            <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-xs font-medium text-foreground">Choose the fake customer situation</p>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                Try both paths before a pilot. This changes only the simulation—no customer data is read and nothing is sent.
              </p>
              {def.nodes.filter((node) => node.type === "condition").map((node) => (
                <div key={node.id} className="flex items-center justify-between gap-3 pt-1">
                  <div className="min-w-0">
                    <p className="truncate text-xs font-medium">{node.label || "Condition"}</p>
                    <p className="truncate text-[10px] text-muted-foreground">
                      {normalizeExceptions(node.parameters.exceptions).map((group) => group.label).join("; ") || "No exceptions configured"}
                    </p>
                  </div>
                  <Select
                    value={testScenario[node.id] ?? "continue"}
                    disabled={busy === "dry_run"}
                    onValueChange={(value) => {
                      if (value !== "continue" && value !== "skip") return;
                      const next: NonNullable<DryRunScenario["conditionOutcomes"]> = {
                        ...testScenario,
                        [node.id]: value,
                      };
                      setTestScenario(next);
                      void runDryRun(next);
                    }}
                  >
                    <SelectTrigger className="h-8 w-[150px] shrink-0 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="continue">Checks pass</SelectItem>
                      <SelectItem value="skip">Exception applies</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
          {def.nodes.some((node) => node.type === "loop") && (
            <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
              <p className="text-xs font-medium text-foreground">How many rounds run</p>
              {def.nodes.filter((node) => node.type === "loop").map((node) => {
                const max = Math.min(MAX_LOOP_ITERATIONS, Math.max(1, Math.floor(Number(node.parameters.maxIterations) || 1)));
                const chosen = Math.min(testLoopRounds[node.id] ?? max, max);
                return (
                  <div key={node.id} className="flex items-center justify-between gap-3 pt-1">
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium">{node.label || "Repeat until"}</p>
                      <p className="truncate text-[10px] text-muted-foreground">
                        Stops when: {normalizeExceptions(node.parameters.stopWhen).map((group) => group.label).join(" or ") || "no stop rules yet"}
                      </p>
                    </div>
                    <Select
                      value={String(chosen)}
                      disabled={busy === "dry_run"}
                      onValueChange={(value) => {
                        const next = { ...testLoopRounds, [node.id]: Number(value) };
                        setTestLoopRounds(next);
                        void runDryRun(undefined, next);
                      }}
                    >
                      <SelectTrigger className="h-8 w-[190px] shrink-0 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">A stop rule already matches</SelectItem>
                        {Array.from({ length: max - 1 }, (_, k) => k + 1).map((k) => (
                          <SelectItem key={k} value={String(k)}>
                            {k} round{k === 1 ? "" : "s"}, then a rule matches
                          </SelectItem>
                        ))}
                        <SelectItem value={String(max)}>All {max} round{max === 1 ? "" : "s"}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>
          )}
          <div className="max-h-[420px] space-y-3 overflow-y-auto pr-1">
            {(dryRun ?? []).map((s, i) => {
              const st = BLOCK_STYLES[s.kind] ?? BLOCK_STYLES.execution;
              const Icon = st.icon;
              return (
                <div key={i} className="flex items-start gap-3">
                  <span className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${st.chip}`}>
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-foreground">
                      {s.label}
                      <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-[10px] font-normal text-muted-foreground">{fmtAt(s.atMinutes)}</span>
                    </p>
                    <p className="text-xs leading-relaxed text-muted-foreground">{s.narration}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">
            No messages are sent during a test. Times are relative to the trigger firing. Test each exception path before turning a workflow on.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkflowBuilder;
